import type { MeDto, PermisEfectiuDto, TipusAcces } from '@/api/types';

export const PERFILS_MOCK = [
  'ADMIN',
  'RESPONSABLE_DEPARTAMENT',
  'RESPONSABLE_SEGURETAT',
  'TECNIC',
  'STANDARD',
] as const;
export type PerfilMock = (typeof PERFILS_MOCK)[number];

const CLAU_PERFIL = 'cai.mock.perfil';

const TOTS: TipusAcces[] = ['CONSULTAR', 'ALTA', 'MODIFICAR', 'ELIMINAR'];

const RECURSOS: Record<string, TipusAcces[]> = {
  'cai.activacions': ['CONSULTAR', 'ALTA', 'MODIFICAR'],
  'cai.activacions.totes': ['CONSULTAR'],
  'cai.activacions.critica': ['ALTA'],
  'cai.activacions.expert': ['ALTA'],
  'cai.activacions.escalat': ['ALTA'],
  'cai.activacions.tancament': ['ALTA'],
  'cai.activacions.itcau': ['MODIFICAR'],
  'cai.guardies': ['CONSULTAR'],
  'cai.calendaris': TOTS,
  'cai.torns': TOTS,
  'cai.sollicituds': ['CONSULTAR', 'MODIFICAR'],
  'cai.organitzacio': TOTS,
  'cai.zones': TOTS,
  'cai.usuaris-informar': TOTS,
  'cai.experts': ['CONSULTAR', 'MODIFICAR'],
  'cai.circuits': TOTS,
  'cai.notificacions.politica': ['CONSULTAR', 'MODIFICAR'],
  'cai.plantilles': TOTS,
  'cai.documents': TOTS,
  'cai.documents.politica': ['CONSULTAR', 'MODIFICAR'],
  'cai.usuaris': ['CONSULTAR', 'ALTA', 'MODIFICAR'],
  'cai.rols': TOTS,
  'cai.informes': ['CONSULTAR', 'ALTA'],
  'cai.auditoria': ['CONSULTAR'],
  'cai.monitoratge': ['CONSULTAR'],
  'cai.integracions': ['CONSULTAR', 'MODIFICAR'],
  'cai.app-versions': TOTS,
  'cai.configuracio': ['CONSULTAR', 'MODIFICAR'],
};

function globals(recursos: Record<string, TipusAcces[]>): PermisEfectiuDto[] {
  return Object.entries(recursos).flatMap(([recurs, tipus]) =>
    tipus.map((tipusAcces) => ({ recurs, tipusAcces, ambit: 'GLOBAL' as const, ambitId: null })),
  );
}

function perDepartament(
  departamentId: number,
  recursos: Record<string, TipusAcces[]>,
): PermisEfectiuDto[] {
  return Object.entries(recursos).flatMap(([recurs, tipus]) =>
    tipus.map((tipusAcces) => ({
      recurs,
      tipusAcces,
      ambit: 'DEPARTAMENT' as const,
      ambitId: departamentId,
    })),
  );
}

function perArea(areaId: number, recursos: Record<string, TipusAcces[]>): PermisEfectiuDto[] {
  return Object.entries(recursos).flatMap(([recurs, tipus]) =>
    tipus.map((tipusAcces) => ({ recurs, tipusAcces, ambit: 'AREA' as const, ambitId: areaId })),
  );
}

const BASE = {
  id: 1,
  upn: 'jordi.noguera@bonarea.com',
  nom: 'Jordi',
  cognoms: 'Noguera',
  email: 'jordi.noguera@bonarea.com',
  telefon: '600000000',
  telefonConfirmat: true,
  idioma: null,
  departament: 'Sistemes',
  arees: [
    { id: 1, nom: 'Sistemes', tipus: 'GUARDIA_IT' as const },
    { id: 4, nom: 'Incendis', tipus: 'CRISI' as const },
  ],
};

export function meMock(perfil: PerfilMock): MeDto {
  switch (perfil) {
    case 'ADMIN':
      return {
        ...BASE,
        rols: [{ codi: 'ADMIN', nom: 'Administrador', ambit: 'GLOBAL' }],
        permisos: globals(RECURSOS),
        pestanyes: ['INICI', 'ACTIVACIONS', 'CRISI', 'GUARDIES', 'PERFIL'],
      };
    case 'RESPONSABLE_DEPARTAMENT':
      return {
        ...BASE,
        rols: [
          {
            codi: 'RESPONSABLE_DEPARTAMENT',
            nom: 'Responsable de departament',
            ambit: 'DEPARTAMENT',
            ambitId: 1,
          },
        ],
        permisos: perDepartament(1, {
          'cai.activacions': ['CONSULTAR', 'ALTA', 'MODIFICAR'],
          'cai.activacions.critica': ['ALTA'],
          'cai.activacions.expert': ['ALTA'],
          'cai.activacions.escalat': ['ALTA'],
          'cai.activacions.tancament': ['ALTA'],
          'cai.activacions.itcau': ['MODIFICAR'],
          'cai.guardies': ['CONSULTAR'],
          'cai.calendaris': TOTS,
          'cai.torns': TOTS,
          'cai.sollicituds': ['CONSULTAR', 'MODIFICAR'],
          'cai.organitzacio': ['CONSULTAR'],
          'cai.zones': ['CONSULTAR'],
          'cai.usuaris-informar': TOTS,
          'cai.experts': ['CONSULTAR', 'MODIFICAR'],
          'cai.circuits': ['CONSULTAR', 'MODIFICAR'],
          'cai.notificacions.politica': ['CONSULTAR'],
          'cai.plantilles': ['CONSULTAR', 'MODIFICAR'],
          'cai.documents': TOTS,
          'cai.usuaris': ['CONSULTAR', 'ALTA'],
          'cai.rols': ['CONSULTAR', 'MODIFICAR'],
          'cai.informes': ['CONSULTAR', 'ALTA'],
          'cai.monitoratge': ['CONSULTAR'],
        }),
        pestanyes: ['INICI', 'GUARDIES', 'SOLLICITUDS', 'ACTIVACIONS', 'PERFIL'],
      };
    case 'TECNIC':
      return {
        ...BASE,
        rols: [{ codi: 'TECNIC_GUARDIA', nom: 'Tècnic de guàrdia', ambit: 'AREA', ambitId: 1 }],
        permisos: perArea(1, {
          'cai.activacions': ['CONSULTAR', 'ALTA', 'MODIFICAR'],
          'cai.activacions.expert': ['ALTA'],
          'cai.activacions.escalat': ['ALTA'],
          'cai.activacions.tancament': ['ALTA'],
          'cai.guardies': ['CONSULTAR'],
          'cai.torns': ['CONSULTAR'],
          'cai.documents': ['CONSULTAR'],
        }),
        pestanyes: ['INICI', 'ACTIVACIONS', 'CRISI', 'GUARDIES', 'PERFIL'],
      };
    case 'RESPONSABLE_SEGURETAT':
      return {
        ...BASE,
        rols: [{ codi: 'RESPONSABLE_SEGURETAT', nom: 'Responsable de seguretat', ambit: 'GLOBAL' }],
        permisos: globals({
          'cai.zones': TOTS,
          'cai.organitzacio': ['CONSULTAR'],
          'cai.activacions': ['CONSULTAR'],
          'cai.activacions.totes': ['CONSULTAR'],
          'cai.guardies': ['CONSULTAR'],
          'cai.informes': ['CONSULTAR'],
          'cai.auditoria': ['CONSULTAR'],
          'cai.monitoratge': ['CONSULTAR'],
        }),
        pestanyes: ['TAULER', 'ACTIVACIONS', 'EVIDENCIES', 'INDICADORS', 'PERFIL'],
      };
    case 'STANDARD':
      return {
        ...BASE,
        rols: [{ codi: 'STANDARD', nom: 'Standard (reporta)', ambit: 'GLOBAL' }],
        permisos: globals({ 'cai.activacions': ['ALTA'] }),
        pestanyes: ['INICI', 'MEUS_REPORTS', 'PERFIL'],
      };
  }
}

export function perfilMockDesat(): PerfilMock {
  try {
    const v = localStorage.getItem(CLAU_PERFIL);
    return (PERFILS_MOCK as readonly string[]).includes(v ?? '') ? (v as PerfilMock) : 'ADMIN';
  } catch {
    return 'ADMIN';
  }
}

export function desaPerfilMock(perfil: PerfilMock): void {
  try {
    localStorage.setItem(CLAU_PERFIL, perfil);
  } catch {
    /* empty */
  }
}
